import React from "react";
import { Box, IconButton, TableRow, TableCell, Tooltip, withStyles } from "@material-ui/core";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import FilterListIcon from "@material-ui/icons/FilterList";

// Styled tooltip with white background and black text
const CustomHeaderTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: "white",
    color: "black",
    fontSize: "0.9rem",
    textAlign: "center",
    border: "0.5px solid lightgrey",
    cursor: "default",
    maxWidth: "300px",
  },
}))(Tooltip);

export const TableHeader = ({
  columns,
  allColumns,
  columnWidths,
  firstRowHeight,
  resizing,
  sortConfig,
  filters,
  onSort,
  onFilterClick,
  onMouseDown,
  firstHeaderRowRef,
  stickyFirstColumn = false,
  hideSubHeaderRow = false,
}) => {
  return (
    <>
      {/* Main header row */}
      <TableRow ref={firstHeaderRowRef}>
        {columns.map((col, colIdx) => {
          const mainKey = `main-${colIdx}`;
          // Calculate total width from sub-columns
          const totalWidth = col.subColumns.reduce((sum, subCol, subIdx) => {
            const subKey = `${colIdx}-${subIdx}`;
            return sum + (columnWidths[subKey] || subCol.width || subCol.minWidth || 120);
          }, 0);
          
          // Use solid hex color for default header background
          const headerBgColor = col.headerBgColor || "#f5f5f5"; // Default to solid grey if not specified
          // Check if main header should be sortable (only one sub-column and main column is sortable)
          const isMainSortable = col.sortable && col.subColumns.length === 1 && col.subColumns[0].field;
          const mainSortField = isMainSortable ? col.subColumns[0].field : null;
          const isMainSorted = mainSortField && sortConfig?.field === mainSortField;
          // Check if main header should be filterable (only one sub-column and sub-column is filterable)
          const isMainFilterable = hideSubHeaderRow && col.subColumns.length === 1 && col.subColumns[0].filterable && col.subColumns[0].field;
          const mainFilterField = isMainFilterable ? col.subColumns[0].field : null;
          const mainFilterType = isMainFilterable ? col.subColumns[0].type : null;
          const isFirstColumn = colIdx === 0 && stickyFirstColumn;
          // For first column, span both header rows if sub-header row is shown
          const shouldSpanRows = isFirstColumn && !hideSubHeaderRow;
          
          return (
            <TableCell
              key={mainKey}
              colSpan={col.subColumns.length}
              rowSpan={shouldSpanRows ? 2 : undefined}
              align={col.align || "center"}
              style={{
                backgroundColor: headerBgColor,
                color: col.headerTextColor || "inherit",
                textAlign: col.align || "center",
                borderRight: "1px solid rgba(224, 224, 224, 1)",
                width: `${totalWidth}px`,
                minWidth: `${totalWidth}px`,
                cursor: isMainSortable ? "pointer" : "default",
                position: "sticky",
                top: 0,
                zIndex: isFirstColumn ? 12 : 11, // Higher z-index for sticky first column
                left: isFirstColumn ? 0 : undefined,
                verticalAlign: hideSubHeaderRow ? "middle" : (shouldSpanRows ? "middle" : "top"),
                fontWeight: 600,
                fontSize: "0.85rem",
                padding: "4px 8px",
                userSelect: "none",
              }}
              onClick={isMainSortable && mainSortField ? (e) => {
                // Don't trigger sort if clicking on the resizer
                if (e.target.closest('.resizer')) {
                  return;
                }
                onSort(mainSortField);
              } : undefined}
            >
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  cursor: isMainSortable ? "pointer" : "inherit",
                  position: "relative",
                  width: "100%",
                }}
                onMouseEnter={(e) => {
                  // Show filter icon on hover of header cell
                  if (isMainFilterable && mainFilterField) {
                    const filterContainer = e.currentTarget.querySelector('.filter-icon-container');
                    if (filterContainer && !filters[mainFilterField]) {
                      filterContainer.style.opacity = "1";
                    }
                  }
                }}
                onMouseLeave={(e) => {
                  // Hide filter icon when leaving header cell (unless filter is active)
                  if (isMainFilterable && mainFilterField) {
                    const filterContainer = e.currentTarget.querySelector('.filter-icon-container');
                    if (filterContainer && !filters[mainFilterField]) {
                      filterContainer.style.opacity = "0";
                    }
                  }
                }}
              >
                {(() => {
                  // Use sub-column tooltip when hideSubHeaderRow is true (main header represents sub-column)
                  // Otherwise use main column tooltip if available
                  const tooltipText = hideSubHeaderRow && col.subColumns.length === 1 && col.subColumns[0].tooltip
                    ? col.subColumns[0].tooltip
                    : col.tooltip;
                  
                  return tooltipText ? (
                    <CustomHeaderTooltip title={tooltipText} placement="top">
                      <Box component="span">{col.label}</Box>
                    </CustomHeaderTooltip>
                  ) : (
                    <Box component="span">{col.label}</Box>
                  );
                })()}
                {/* Sort and Filter icons row below text - shown when hideSubHeaderRow is true */}
                {(isMainSortable || isMainFilterable) && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                    }}
                  >
                    {/* Sorting icons */}
                    {isMainSortable && isMainSorted && (
                      <Box 
                        className="sort-icon-container"
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          cursor: "pointer",
                        }}
                      >
                        {sortConfig?.direction === "asc" ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    )}
                    {/* Filter icon - shown on hover or when filter is active */}
                    {isMainFilterable && mainFilterField && (
                      <Box
                        className="filter-icon-container"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          opacity: filters[mainFilterField] ? 1 : 0,
                          transition: "opacity 0.2s",
                        }}
                      >
                        <IconButton 
                          size="small" 
                          style={{ 
                            padding: 2,
                            minWidth: "auto",
                            width: "20px",
                            height: "20px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sort when clicking filter
                            onFilterClick(e, mainFilterField, mainFilterType);
                          }}
                        >
                          <FilterListIcon 
                            fontSize="small" 
                            style={{ 
                              color: filters[mainFilterField] ? "inherit" : "inherit",
                            }}
                          />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                className="resizer"
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent triggering sort when clicking resize button
                  e.preventDefault(); // Also prevent default behavior
                  onMouseDown(e, mainKey, true);
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "5px",
                  height: "100%",
                  cursor: "col-resize",
                  zIndex: 1,
                  backgroundColor: resizing?.key === mainKey ? "#1976d2" : "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#1976d2";
                  e.currentTarget.style.opacity = "0.5";
                }}
                onMouseLeave={(e) => {
                  if (resizing?.key !== mainKey) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.opacity = "1";
                  }
                }}
              />
            </TableCell>
          );
        })}
      </TableRow>
      {/* Sub-header row - conditionally rendered */}
      {!hideSubHeaderRow && (
      <TableRow>
        {allColumns.map(({ main, sub, mainIdx, subIdx }) => {
          // Skip first column's sub-columns if it spans rows
          const isFirstColumn = mainIdx === 0 && stickyFirstColumn;
          if (isFirstColumn) {
            return null;
          }
          
          const key = `${mainIdx}-${subIdx}`;
          const width = columnWidths[key] || sub.width || sub.minWidth || 120;
          
          // Ensure solid background color for headers
          const subHeaderBgColor = sub.headerBgColor || main.headerBgColor || "#f5f5f5";
          const isSubSorted = sub.sortable && sub.field && sortConfig?.field === sub.field;
          
          // Calculate left position for sticky first column
          const leftPosition = isFirstColumn ? 0 : undefined;
          
          return (
            <TableCell
              key={key}
              align={sub.align || "center"}
              style={{
                backgroundColor: subHeaderBgColor,
                color: sub.headerTextColor || main.headerTextColor || "inherit",
                textAlign: sub.align || "center",
                minWidth: sub.minWidth || 50,
                width: `${width}px`,
                cursor: sub.sortable ? "pointer" : "default",
                position: "sticky",
                top: `${firstRowHeight}px`, // Dynamic height of first header row
                zIndex: isFirstColumn ? 12 : 10, // Higher z-index for sticky first column
                left: leftPosition,
                borderRight: "1px solid rgba(224, 224, 224, 1)",
                fontWeight: 600,
                fontSize: "0.85rem",
                padding: "4px 8px",
                userSelect: "none",
              }}
              onClick={sub.sortable && sub.field ? (e) => {
                // Don't trigger sort if clicking on the resizer
                if (e.target.closest('.resizer')) {
                  return;
                }
                onSort(sub.field);
              } : undefined}
            >
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  cursor: sub.sortable ? "pointer" : "inherit",
                  position: "relative",
                  width: "100%",
                }}
              >
                {sub.tooltip ? (
                  <CustomHeaderTooltip title={sub.tooltip} placement="top">
                    <Box component="span" style={{ textAlign: "center" }}>{sub.label}</Box>
                  </CustomHeaderTooltip>
                ) : (
                  <Box component="span" style={{ textAlign: "center" }}>{sub.label}</Box>
                )}
                {/* Custom sub-label (e.g., "Edit Here") */}
                {sub.headerSubLabel && (
                  <Box
                    component="span"
                    style={{
                      fontSize: "0.75rem",
                      textAlign: "center",
                      padding: "2px 4px",
                      borderRadius: "2px",
                      ...(sub.headerSubLabelStyle || {
                        background: "rgb(191, 143, 0)",
                        color: "white",
                      }),
                    }}
                  >
                    {sub.headerSubLabel}
                  </Box>
                )}
                {/* Sort and Filter icons row below text */}
                {(sub.sortable || (sub.filterable && sub.field)) && (
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 4,
                    }}
                  >
                    {/* Sorting icons */}
                    {sub.sortable && isSubSorted && (
                      <Box 
                        className="sort-icon-container"
                        style={{ 
                          display: "flex", 
                          alignItems: "center", 
                          cursor: "pointer",
                        }}
                      >
                        {sortConfig?.direction === "asc" ? (
                          <ArrowUpwardIcon fontSize="small" />
                        ) : (
                          <ArrowDownwardIcon fontSize="small" />
                        )}
                      </Box>
                    )}
                    {/* Filter icon - shown on hover or when filter is active */}
                    {sub.filterable && sub.field && (
                      <Box
                        className="filter-icon-container"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          opacity: filters[sub.field] ? 1 : 0,
                          transition: "opacity 0.2s",
                        }}
                      >
                        <IconButton 
                          size="small" 
                          style={{ 
                            padding: 2,
                            minWidth: "auto",
                            width: "20px",
                            height: "20px",
                          }}
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent sort when clicking filter
                            // Use dataType if available (for more accurate filtering), otherwise use type
                            onFilterClick(e, sub.field, sub.dataType || sub.type);
                          }}
                        >
                          <FilterListIcon 
                            fontSize="small" 
                            style={{ 
                              color: filters[sub.field] ? "#1976d2" : "inherit",
                            }}
                          />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
              <Box
                className="resizer"
                onMouseDown={(e) => {
                  e.stopPropagation(); // Prevent triggering sort when clicking resize button
                  e.preventDefault(); // Also prevent default behavior
                  onMouseDown(e, key);
                }}
                style={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "5px",
                  height: "100%",
                  cursor: "col-resize",
                  zIndex: 1,
                  backgroundColor: resizing?.key === key ? "#1976d2" : "transparent",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#1976d2";
                  e.currentTarget.style.opacity = "0.5";
                }}
                onMouseLeave={(e) => {
                  if (resizing?.key !== key) {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.opacity = "1";
                  }
                }}
              />
            </TableCell>
          );
        })}
      </TableRow>
      )}
    </>
  );
};

